import {
  AppIdentifier,
  ApplicationConfigList,
  BaseEnv,
  BroadcastMessage,
  CookieConsent,
  Features,
  FontConfig,
  Frame,
  KeycloakAuth,
  PlausibleConfig,
  ReportConfig,
  ThemeConfig
} from '@aipb/env/model';
import { isNil } from '@aipb/util/other';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatCardConfig } from '@angular/material/card';
import { MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { catchError, of, tap } from 'rxjs';

export interface AppConfigModel extends BaseEnv {
  ENVIRONMENT?: string;
  SENTRY_DSN?: string;
  SENTRY_VERSION_PACKAGE_NAME?: string;
  PRODUCTION?: boolean;
  GULLIVER_API_URL?: string;
  PROJECT?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private http = inject(HttpClient);

  private _config?: Partial<AppConfigModel> = {};

  get config() {
    return this._config;
  }

  get environment(): string {
    // @ts-expect-error foo
    return this._config.ENVIRONMENT || 'local';
  }

  get project(): string {
    // @ts-expect-error foo
    return this._config.PROJECT || 'unknown';
  }

  get sentryDsn(): string | undefined {
    // @ts-expect-error foo
    return this._config.SENTRY_DSN || undefined;
  }

  get sentryVersionPackageName(): string | undefined {
    // @ts-expect-error foo
    return this._config.SENTRY_VERSION_PACKAGE_NAME || undefined;
  }

  get production(): boolean {
    // @ts-expect-error foo
    return this._config.PRODUCTION || false;
  }

  get matFormFieldDefaultOptions(): MatFormFieldDefaultOptions {
    return this._config?.theme?.mat_form_field_default_options || {};
  }

  get matCardDefaultOptions(): MatCardConfig {
    return (
      this._config?.theme?.mat_card_default_options || {
        appearance: 'outlined'
      }
    );
  }

  get resolverApiUrl(): string {
    // @ts-expect-error foo
    return this._config.resolverApiUrl;
  }

  get dropboxWrapperApiBaseUrl(): string {
    // @ts-expect-error foo
    return this._config.dropboxWrapperApiBaseUrl;
  }

  get features(): Features {
    // @ts-expect-error foo
    return this._config.features || {};
  }

  get translations() {
    // @ts-expect-error foo
    return this._config.translations;
  }

  get broadcastMessage(): BroadcastMessage {
    // @ts-expect-error foo
    return this._config.broadcastMessage;
  }

  get theme(): ThemeConfig {
    // @ts-expect-error foo
    return this._config.theme;
  }

  get frame(): Frame {
    // @ts-expect-error foo
    return this._config.frame;
  }

  get cookieConsent(): CookieConsent {
    // @ts-expect-error foo
    return this._config.cookieConsent;
  }

  get googleAnalyticsId(): string {
    // @ts-expect-error foo
    return this._config.googleAnalyticsId;
  }

  get fonts(): FontConfig[] {
    // @ts-expect-error foo
    return this._config.fonts || [];
  }

  get applications(): ApplicationConfigList {
    // @ts-expect-error foo
    return this._config.applications;
  }

  get plausible(): PlausibleConfig {
    // @ts-expect-error foo
    return this._config.plausible;
  }

  get scripts(): string[] {
    // @ts-expect-error foo
    return this._config.scripts || [];
  }

  get hasBrowser() {
    return this.hasApp('browser');
  }

  get hasBrowserMirador() {
    return this.hasApp('browser-mirador');
  }

  get coopEnabled() {
    // @ts-expect-error foo
    return this._config.coopEnabled || false;
  }

  get overlayEnabled() {
    // @ts-expect-error foo
    return this._config.overlayEnabled || false;
  }

  get personalContentEnabled(): boolean {
    // @ts-expect-error foo
    return ('personalContent' in this._config && !isNil(this._config.personalContent)) || false;
  }

  get personalContentApiUrl(): string {
    // @ts-expect-error foo
    const personalContent = this._config.personalContent;

    if (!personalContent) {
      return '';
    }

    return personalContent.apiUrl;
  }

  get personalContentAddingNewItemDisabled() {
    // @ts-expect-error foo
    const personalContent = this._config.personalContent;

    if (!personalContent) {
      return true;
    }

    return personalContent.addingNewItemDisabled || false;
  }

  get report(): ReportConfig {
    return (
      // @ts-expect-error foo
      this._config.report || {
        linkColor: 'primary',
        toolbarEnabled: false
      }
    );
  }

  get footerReference(): string {
    // @ts-expect-error foo
    return this._config.footerReference;
  }

  get auth(): KeycloakAuth {
    // @ts-expect-error foo
    return this._config.auth;
  }

  loadConfig() {
    return this.http.get<AppConfigModel>(`assets/config.json?t=${new Date().getTime()}`).pipe(
      catchError(() => of({})),
      tap((data) => {
        this._config = data;
      })
    );
  }

  private hasApp(appId: AppIdentifier): boolean {
    // @ts-expect-error foo
    return appId in this._config.applications;
  }
}
